/**
 * Parse reference pattern from text
 *
 * Extracts pattern like: [text](referenceId=value)\nquestion or [text](id=value)\nquestion
 *
 * @example
 * ```ts
 * const result = parseReferencePattern("[你好呀?](referenceId=123)\nBTc");
 * // Returns: {
 * //   referenceContent: "你好呀?",
 * //   key: "referenceId",
 * //   value: "123",
 * //   hasNewline: true,
 * //   question: "BTc"
 * // }
 *
 * const result2 = parseReferencePattern("[Text](id=456)");
 * // Returns: {
 * //   referenceContent: "Text",
 * //   key: "id",
 * //   value: "456",
 * //   hasNewline: false,
 * //   question: undefined
 * // }
 * ```
 */

type QuestionContentResult = {
  referenceContent?: string;
  question: string;
};

export type UserMessagePresentation =
  | {
      kind: "user-input";
      text: string;
      referenceContent?: string;
    }
  | {
      kind: "action-echo";
      text: string;
    }
  | {
      kind: "silent-entry";
      text: "";
    };

export interface ReferencePatternResult {
  /** Text content inside square brackets */
  referenceContent: string;
  /** Key name ("referenceId", "id" or "referenceType") */
  key: string;
  /** Value (e.g., 123) */
  value: string;
  /** Whether contains newline character */
  hasNewline: boolean;
  /** Question text after the pattern */
  question?: string;
}

/**
 * Parse a full reference protocol message.
 *
 * The protocol marker must occupy the first line. Any following content must be
 * separated by a real or escaped newline.
 *
 * @param text - Input text to parse
 * @returns Parsed result or null if pattern doesn't match
 */
export const parseReferencePatternStrict = (
  text: string,
): ReferencePatternResult | null => {
  // Capture groups: 1=referenceContent, 2=key, 3=value, 4=newline, 5=question
  // Value is captured until space or closing parenthesis (more precise value extraction)
  // Note: [^\]]* allows empty brackets (changed from + to *)
  const pattern =
    /^\[([^\]]*)\]\(.*?(referenceId|id|referenceType)=([^\s)]+).*?\)(?:(\n|\\n)([\s\S]*))?$/;

  const match = text.match(pattern);

  if (!match) {
    return null;
  }

  return {
    referenceContent: match[1] || "",
    key: match[2] || "",
    value: match[3] || "",
    hasNewline: !!match[4],
    question: match[5],
  };
};

/**
 * 获取解析 question 内容
 * @param content - Content to parse
 * @returns Question content result
 *
 * 协议语义：首行是 reference 协议行时，question = 正文；正文为空（Ed-first
 * 消息）则 question 为空串，**不回退全文** —— 否则裸协议行会渲染成标题/气泡。
 */
export const getReferencePattern = (content: string): QuestionContentResult => {
  const result = parseReferencePatternStrict(content);
  if (result) {
    return {
      referenceContent: result.referenceContent,
      question: result.question ?? "",
    };
  }
  return {
    referenceContent: undefined,
    question: content,
  };
};

/**
 * Whether the message consists only of a valid reference protocol line.
 */
export const isReferenceOnlyMessage = (content: unknown): boolean => {
  if (typeof content !== "string" || !content) {
    return false;
  }
  const parsed = parseReferencePatternStrict(content.replace(/\r\n/g, "\n"));
  return !!parsed && !(parsed.question ?? "").trim();
};

/**
 * Derive user-message presentation from protocol shape rather than referenceType.
 */
export const resolveUserMessagePresentation = (
  content: unknown,
): UserMessagePresentation => {
  const raw = typeof content === "string" ? content : "";

  if (isReferenceOnlyMessage(raw)) {
    const label = getReferencePattern(raw).referenceContent?.trim() ?? "";
    return label
      ? { kind: "action-echo", text: label }
      : { kind: "silent-entry", text: "" };
  }

  const { referenceContent, question } = getReferencePattern(raw);
  return {
    kind: "user-input",
    text: question,
    ...(referenceContent?.trim()
      ? { referenceContent: referenceContent.trim() }
      : {}),
  };
};
